import { useMemo } from 'react';
import {
  CAPABILITIES,
  COMMERCIAL_RESTRICTION_DATE,
  planIncludes,
  planIsMissingCapability,
  subscriptionIsKnown,
  upgradeMessage,
  upgradeTargetFor,
} from '../helpers/planEntitlements';


// Single source of truth for the frontend — commercialFilterUtils.js and the
// property-type dropdown both import from here rather than keeping copies.
// Kept in step with Search::getCommercialCategories() and
// PropertyExportController::isCommercialProperty() on the backend.
export const COMMERCIAL_CATEGORIES = [
  'Light Manufacturing',
  'Commercial',
  'Medical Building or Clinic',
  'Mini-Warehouse or Self-Storage',
  'Church',
  'Agricultural/Rural',
  'Apartments',
  'Office',
  'Auto Repair or Garage',
  'Office Building (General)',
  'Commercial Condominium (Not Offices)',
  'Parcel with Improvements',
  'Commercial Office',
  'Condominium Offices',
  'Public School',
  'Convenience Store',
  'Day Care or Preschool Facility',
  'Recreational',
  'Restaurant',
  'Exempt',
  'Retail Stores',
  'Fast Food Restaurant or Drive-Through',
  'Financial Building or Bank',
  'Governmental or Public Use',
  'Warehouse (Industrial)',
  'Industrial',
  'Wholesale Outlet or Discount Store'
];

// Re-exported so the many modules that import it from here keep working; the
// date itself, and every rule that reads it, now live in planEntitlements.
export { COMMERCIAL_RESTRICTION_DATE, subscriptionIsKnown, upgradeMessage, upgradeTargetFor };

/**
 * Check if a property is commercial based on its type
 */
export const isPropertyCommercial = (propertyType) => {
  if (!propertyType) return false;
  const typeLower = String(propertyType).toLowerCase();
  return COMMERCIAL_CATEGORIES.some(cat => cat.toLowerCase() === typeLower);
};

/**
 * Check if a user should be restricted from viewing commercial properties.
 *
 * Fail-closed: anything we cannot positively confirm as entitled returns false.
 * Use userIsRestricted() for UI gating, which additionally waits until the
 * plan is actually known.
 */
export const userCanAccessCommercial = (memData) =>
  planIncludes(memData, CAPABILITIES.COMMERCIAL_PROPERTIES);

/**
 * The gate every piece of UI should use: restrict only once we positively know
 * the member is on a plan that does not include the capability.
 */
export const userIsRestricted = (memData) =>
  planIsMissingCapability(memData, CAPABILITIES.COMMERCIAL_PROPERTIES);

/**
 * Investor Portfolio follows the same built-in rule as the commercial
 * categories — new Lite/Pro lose it, Legend keeps it, existing subscribers are
 * grandfathered — but it resolves as its own capability, so an Enterprise
 * agreement can include one without the other.
 */
export const userCanAccessPortfolio = (memData) =>
  planIncludes(memData, CAPABILITIES.INVESTOR_PORTFOLIO);
export const userIsRestrictedFromPortfolio = (memData) =>
  planIsMissingCapability(memData, CAPABILITIES.INVESTOR_PORTFOLIO);

/**
 * Owner contact information — the emails and phone numbers behind "View
 * contact". Included on every public plan — Lite and Pro too; the Lite/Pro
 * restriction is commercial and portfolio only. It resolves as its own
 * capability so an Enterprise agreement can still exclude it.
 */
export const userCanAccessContactInformation = (memData) =>
  planIncludes(memData, CAPABILITIES.CONTACT_INFORMATION);
export const userIsRestrictedFromContactInformation = (memData) =>
  planIsMissingCapability(memData, CAPABILITIES.CONTACT_INFORMATION);

/** Weather Reports, previously gated against a hard-coded plan id. */
export const userCanAccessWeather = (memData) =>
  planIncludes(memData, CAPABILITIES.WEATHER_DATA);

/**
 * Hook to check if a property is restricted for the current user
 */
export const useCommercialAccess = (property, memData) => {
  return useMemo(() => {
    const isCommercial = isPropertyCommercial(
      property?.general_propertyTypeDetail || property?.general_propertyTypeCategory
    );
    const userCanAccess = userCanAccessCommercial(memData);

    return {
      isCommercial,
      userCanAccess,
      isRestricted: isCommercial && userIsRestricted(memData),
    };
    // The server's answer is part of the input now, so it has to be part of
    // the dependency list — otherwise a member whose agreement includes
    // commercial keeps the memoised "restricted" from before memData landed.
    //
    // Depend on the whole memData, not on mem_subscription's fields: the
    // entitlement helpers resolve the subscription as
    // `mem_subscription || user_subscription_exist`, so a field list rooted at
    // mem_subscription can miss the source the answer was actually read from.
    // memData is a Redux value replaced wholesale on each /site-settings
    // fetch, so this is a cheap identity check, not a deep compare.
  }, [
    property?.general_propertyTypeDetail,
    property?.general_propertyTypeCategory,
    memData,
  ]);
};
